import * as Yup from "yup";

import {
  fieldConfigurations,
  FieldConfiguration,
  PIC_FIELD_INDICES,
  NOTES_FIELD_INDICES,
} from "./fieldConfigurations";
import {
  agentByCommissionField,
  agentRequiredMessage,
  getLabelledValidation,
  matchesExistingBusiness,
  unmatchedBusinessMessage,
} from "./validationSchema";
import { normaliseColumnName } from "lib/sideLoadedFieldMapping";
import {
  parsePicValues,
  PIC_DISPLAY_DELIMITER,
} from "constants/sideLoadedBusinesses";

// A blank cell is not a mapping: the compare modal only offers columns that
// hold something, so a column mapped here but empty for this record would
// validate against a value the user cannot see.
const hasValue = (
  rawData: Record<string, string | number>,
  sourceColumn: string,
): boolean =>
  rawData[sourceColumn] !== undefined &&
  rawData[sourceColumn] !== null &&
  rawData[sourceColumn] !== "";

const allConfigurations = Object.values(fieldConfigurations);

const suggestionSets = new WeakMap<FieldConfiguration, Set<string>>();

const getSuggestionSet = (configuration: FieldConfiguration): Set<string> => {
  let suggestions = suggestionSets.get(configuration);
  if (!suggestions) {
    suggestions = new Set(configuration.sourceFieldSuggestions);
    suggestionSets.set(configuration, suggestions);
  }
  return suggestions;
};

// A stored mapping wins over guessing, the same order of precedence the compare
// modal applies, so a record reads the same in the grid as it does in the modal.
function getCandidateColumns(
  configurations: FieldConfiguration[],
  sourceColumns: string[],
  entries: SideLoadedDataMappingEntry[] | null | undefined,
): string[][] {
  if (entries?.length) {
    return configurations.map(configuration =>
      entries
        .filter(entry => entry.targetFieldKey === configuration.value)
        .map(entry => entry.sourceColumn),
    );
  }
  const normalisedColumns = sourceColumns.map(normaliseColumnName);
  return configurations.map(configuration => {
    const suggestions = getSuggestionSet(configuration);
    return sourceColumns.filter((_, index) =>
      suggestions.has(normalisedColumns[index]),
    );
  });
}

// Records in a data set share their columns.
const candidateColumnsCache = new WeakMap<
  FieldConfiguration[],
  Map<SideLoadedDataMappingEntry[] | null, Map<string, string[][]>>
>();

function getCachedCandidateColumns(
  configurations: FieldConfiguration[],
  sourceColumns: string[],
  entries: SideLoadedDataMappingEntry[] | null | undefined,
): string[][] {
  let byEntries = candidateColumnsCache.get(configurations);
  if (!byEntries) {
    byEntries = new Map();
    candidateColumnsCache.set(configurations, byEntries);
  }
  const entriesKey = entries?.length ? entries : null;
  let byColumns = byEntries.get(entriesKey);
  if (!byColumns) {
    byColumns = new Map();
    byEntries.set(entriesKey, byColumns);
  }
  const columnsKey = sourceColumns.join("\u0000");
  let candidates = byColumns.get(columnsKey);
  if (!candidates) {
    candidates = getCandidateColumns(configurations, sourceColumns, entriesKey);
    byColumns.set(columnsKey, candidates);
  }
  return candidates;
}

interface AutoMappedField {
  mappableFieldValue: string | boolean | null;
  value: string | number;
  // Notes and PICs gather several columns, one per numbered slot.
  slotColumns: string[] | null;
}

function getAutoMappedField(
  configuration: FieldConfiguration,
  rawData: Record<string, string | number>,
  claimedColumns: string[],
): AutoMappedField {
  if (!claimedColumns.length) {
    return { mappableFieldValue: null, value: "", slotColumns: null };
  }

  if (configuration.handleAsProperty) {
    // Columns past the last slot are dropped.
    const slotColumns = claimedColumns.slice(0, PIC_FIELD_INDICES.length);
    return {
      mappableFieldValue: true,
      value: parsePicValues(
        slotColumns.map(sourceColumn => rawData[sourceColumn]?.toString()),
      ).join(PIC_DISPLAY_DELIMITER),
      slotColumns,
    };
  }

  if (configuration.handleAsNotes) {
    const slotColumns = claimedColumns.slice(0, NOTES_FIELD_INDICES.length);
    return {
      mappableFieldValue: true,
      value: slotColumns
        .filter(sourceColumn => rawData[sourceColumn] !== undefined)
        .map(sourceColumn => `${sourceColumn}: ${rawData[sourceColumn]}`)
        .join("\n"),
      slotColumns,
    };
  }

  // Last claim wins - one target takes a single column.
  const sourceColumn = claimedColumns[claimedColumns.length - 1];
  return {
    mappableFieldValue: sourceColumn,
    value: rawData[sourceColumn],
    slotColumns: null,
  };
}

function getClaimedColumns(
  rawData: Record<string, string | number>,
  configurations: FieldConfiguration[],
  entries: SideLoadedDataMappingEntry[] | null | undefined,
): string[][] {
  return getCachedCandidateColumns(
    configurations,
    Object.keys(rawData),
    entries,
  ).map(candidates =>
    candidates.length
      ? candidates.filter(sourceColumn => hasValue(rawData, sourceColumn))
      : candidates,
  );
}

export function getInitialAutoMappedValues(
  rawData: Record<string, string | number> = {},
  configurations: FieldConfiguration[] = allConfigurations,
  entries?: SideLoadedDataMappingEntry[] | null,
): Record<string, string | number | boolean | null> {
  const safeRawData = rawData || {};
  const claimedColumns = getClaimedColumns(
    safeRawData,
    configurations,
    entries,
  );
  return configurations.reduce((acc, configuration, configurationIndex) => {
    const { mappableFieldValue, value, slotColumns } = getAutoMappedField(
      configuration,
      safeRawData,
      claimedColumns[configurationIndex],
    );
    acc[`MappableField__${configuration.value}`] = mappableFieldValue;
    acc[`Value__${configuration.value}`] = value;
    if (slotColumns) {
      const fieldIndices = configuration.handleAsProperty
        ? PIC_FIELD_INDICES
        : NOTES_FIELD_INDICES;
      slotColumns.forEach((sourceColumn, position) => {
        acc[
          `MappableField__${configuration.value}__${fieldIndices[position]}`
        ] = sourceColumn;
      });
    }
    return acc;
  }, {});
}

export interface SideLoadedValidationOptions {
  businessIdByAgentCodeLookup?: Record<string, string>;
  businessIdBySaleyardCodeLookup?: Record<string, string>;
  configurations?: FieldConfiguration[];
  entries?: SideLoadedDataMappingEntry[] | null;
}

function collectValidationIssues(
  record: SideLoadedBusiness,
  {
    businessIdByAgentCodeLookup = {},
    businessIdBySaleyardCodeLookup = {},
    configurations = allConfigurations,
    entries = null,
  }: SideLoadedValidationOptions,
  firstOnly: boolean,
): string[] {
  const rawData = record?.rawData || {};
  const lookups = {
    rawData,
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
  };

  // Mirrors buildSideLoadedValidationSchema. Unmapped fields can't fail.
  const claimedColumns = getClaimedColumns(rawData, configurations, entries);
  const mappedFields = new Map<FieldConfiguration, AutoMappedField>();
  const mappedFieldValues = new Set<string>();
  configurations.forEach((configuration, configurationIndex) => {
    if (claimedColumns[configurationIndex].length) {
      mappedFields.set(
        configuration,
        getAutoMappedField(
          configuration,
          rawData,
          claimedColumns[configurationIndex],
        ),
      );
      mappedFieldValues.add(configuration.value);
    }
  });

  const issues: string[] = [];
  for (const [configuration, { mappableFieldValue, value }] of mappedFields) {
    if (firstOnly && issues.length) {
      break;
    }
    const agentConfiguration = agentByCommissionField[configuration.value];
    if (configuration.isBusinessLookup) {
      if (
        !matchesExistingBusiness(mappableFieldValue as string | null, lookups)
      ) {
        issues.push(unmatchedBusinessMessage(configuration));
      }
    } else if (agentConfiguration) {
      if (!mappedFieldValues.has(agentConfiguration.value)) {
        issues.push(agentRequiredMessage(configuration, agentConfiguration));
      }
    } else if (configuration.validation) {
      try {
        getLabelledValidation(configuration).validateSync(value, {
          abortEarly: firstOnly,
        });
      } catch (error) {
        if (!(error instanceof Yup.ValidationError)) {
          throw error;
        }
        issues.push(...error.errors);
      }
    }
  }
  return issues;
}

export const getSideLoadedBusinessValidationIssues = (
  record: SideLoadedBusiness,
  options: SideLoadedValidationOptions = {},
): string[] => collectValidationIssues(record, options, false);

export const isSideLoadedBusinessValid = (
  record: SideLoadedBusiness,
  options: SideLoadedValidationOptions = {},
): boolean => collectValidationIssues(record, options, true).length === 0;
